import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import CategoryBadge from "../shared/components/CategoryBadge";

export default function LeadsTab({ showToast }) {
  const navigate = useNavigate();

  // ── Own Firebase subscription — only mounts when this tab is active ───────
  const [leadIndex, setLeadIndex] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [filterStatus, setFilterStatus] = useState("flagged");

  // ── Delete confirm state — tracks which lead is in "Sure?" mode ──────────
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "leadIndex"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLeadIndex(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingLeads(false);
      },
      (err) => {
        console.error("Failed to fetch leadIndex:", err);
        setLoadingLeads(false);
      },
    );
    return () => unsub();
  }, []);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: leadIndex.length,
      flagged: leadIndex.filter((l) => l.status === "flagged").length,
      pending: leadIndex.filter((l) => l.status === "pending").length,
      approved: leadIndex.filter((l) => l.status === "approved").length,
      converted: leadIndex.filter((l) => l.orderConverted).length,
      rejected: leadIndex.filter((l) => l.status === "rejected").length,
    }),
    [leadIndex],
  );

  const filteredLeads = useMemo(
    () =>
      filterStatus === "all"
        ? leadIndex
        : leadIndex.filter((l) => l.status === filterStatus),
    [leadIndex, filterStatus],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleApproveLead = useCallback(
    async (lead) => {
      const batch = writeBatch(db);
      batch.update(doc(db, "users", lead.ownerUid, "leads", lead.id), {
        status: "approved",
        autoApproveAt: null,
        flaggedMatches: [],
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        status: "approved",
        autoApproveAt: null,
        flaggedMatches: [],
      });
      await batch.commit();
      showToast(`Lead "${lead.buyerName}" approved.`);
    },
    [showToast],
  );

  const handleRejectLead = useCallback(
    async (lead) => {
      const batch = writeBatch(db);
      batch.update(doc(db, "users", lead.ownerUid, "leads", lead.id), {
        status: "rejected",
        autoApproveAt: null,
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        status: "rejected",
        autoApproveAt: null,
      });
      await batch.commit();
      showToast(`Lead "${lead.buyerName}" rejected.`, "error");
    },
    [showToast],
  );

  // ── Admin direct delete — two-click inline confirm ────────────────────────
  const handleDeleteClick = useCallback(
    async (lead) => {
      if (deleteConfirmId !== lead.id) {
        // First click — enter "Sure?" mode
        setDeleteConfirmId(lead.id);
        return;
      }
      // Second click — execute delete from both collections
      try {
        const batch = writeBatch(db);
        batch.delete(doc(db, "users", lead.ownerUid, "leads", lead.id));
        batch.delete(doc(db, "leadIndex", lead.id));
        await batch.commit();
        setDeleteConfirmId(null);
        showToast(`Lead "${lead.buyerName}" deleted.`, "error");
      } catch (err) {
        console.error("Failed to delete lead:", err);
        showToast("Failed to delete lead.", "error");
      }
    },
    [deleteConfirmId, showToast],
  );

  // ── Confirm a merchandiser's delete request ───────────────────────────────
  const handleConfirmDeleteRequest = useCallback(
    async (lead) => {
      if (deleteConfirmId !== lead.id) {
        setDeleteConfirmId(lead.id);
        return;
      }
      try {
        const batch = writeBatch(db);
        batch.delete(doc(db, "users", lead.ownerUid, "leads", lead.id));
        batch.delete(doc(db, "leadIndex", lead.id));
        await batch.commit();
        setDeleteConfirmId(null);
        showToast(`Lead "${lead.buyerName}" deleted.`, "error");
      } catch (err) {
        console.error("Failed to delete lead:", err);
        showToast("Failed to delete lead.", "error");
      }
    },
    [deleteConfirmId, showToast],
  );

  // ── Dismiss a merchandiser's delete request — restore previous status ─────
  const handleDismissDeleteRequest = useCallback(
    async (lead) => {
      const restoreStatus = lead.previousStatus || "approved";
      const batch = writeBatch(db);
      batch.update(doc(db, "users", lead.ownerUid, "leads", lead.id), {
        status: restoreStatus,
        deleteRequested: false,
        previousStatus: null,
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        status: restoreStatus,
        deleteRequested: false,
        previousStatus: null,
      });
      await batch.commit();
      showToast(`Delete request for "${lead.buyerName}" dismissed.`);
    },
    [showToast],
  );

  return (
    <>
      {/* ── Overview ── */}
      <h2 className={styles.sectionTitle}>Leads Overview</h2>
      <div className={styles.statsRow}>
        <StatCard
          label="Total Leads"
          value={stats.total}
          sub="across all merchandisers"
        />
        <StatCard
          label="Flagged"
          value={stats.flagged}
          sub="require manual review"
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          sub="auto-approving in 10 min"
        />
        <StatCard label="Approved" value={stats.approved} sub="active leads" />
        <StatCard label="Converted" value={stats.converted} sub="orders won" />
        <StatCard
          label="Rejected"
          value={stats.rejected}
          sub="conflicts resolved"
        />
      </div>

      <div className={styles.rule} />

      {/* ── Table ── */}
      <h2 className={styles.sectionTitle}>Lead Approvals</h2>
      {loadingLeads ? (
        <p className={styles.stateText}>Loading leads…</p>
      ) : (
        <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
          <div className={styles.tableControls}>
            <div className={styles.tableFilters}>
              <div className={styles.tableFilterGroup}>
                <span className={styles.tableFilterLabel}>Status</span>
                <SelectWrapper
                  value={filterStatus}
                  selectClassName={styles.tableFilterSelect}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setDeleteConfirmId(null);
                  }}
                >
                  <option value="all">All</option>
                  <option value="flagged">Flagged</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="delete">Delete Requested</option>
                </SelectWrapper>
              </div>
            </div>
          </div>

          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.th}>#</th>
                  <th className={styles.th}>Buyer</th>
                  <th className={styles.th}>Country</th>
                  <th className={styles.th}>Created By</th>
                  <th className={styles.th}>Handled By</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Flagged Matches</th>
                  <th className={styles.th}>View</th>
                  <th className={styles.th}>Actions</th>
                  <th className={styles.th}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, index) => (
                  <tr
                    key={lead.id}
                    className={styles.tableRow}
                    style={
                      lead.status === "delete"
                        ? { opacity: 0.75, background: "rgba(200,80,80,0.05)" }
                        : {}
                    }
                  >
                    <td className={styles.td}>{index + 1}</td>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {lead.buyerName}
                    </td>
                    <td className={styles.td}>{lead.country}</td>
                    <td className={styles.td}>
                      {lead.createdByName || lead.ownerName}
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.supplierLink}
                        onClick={() =>
                          navigate(`leads/${lead.ownerUid}`, lead.ownerName)
                        }
                      >
                        {lead.ownerName}
                      </button>
                    </td>
                    <td className={styles.td}>
                      <CategoryBadge category={lead.status} />
                    </td>
                    <td className={styles.td}>
                      {lead.flaggedMatches?.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {lead.flaggedMatches.map((m, i) => (
                            <span
                              key={i}
                              style={{
                                fontSize: 10,
                                color: "var(--text-muted)",
                                lineHeight: 1.5,
                              }}
                            >
                              <button
                                className={styles.supplierLink}
                                style={{ fontSize: 10 }}
                                onClick={() => navigate(`/leads/${m.ownerUid}`)}
                              >
                                {m.ownerName}
                              </button>
                              {" — "}
                              <span style={{ color: "var(--gold)" }}>
                                {m.buyerName}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ opacity: 0.35 }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.editBtn}
                        onClick={() => navigate(`/leads/${lead.ownerUid}`)}
                        title="Open lead page"
                      >
                        ↗
                      </button>
                    </td>

                    {/* ── Actions column ── */}
                    <td className={styles.td}>
                      {/* Delete requested by merchandiser — Confirm / Dismiss */}
                      {lead.status === "delete" && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            style={{
                              padding: "5px 12px",
                              fontSize: 9,
                              marginTop: 0,
                              color: "#e08080",
                              borderColor: "rgba(220,100,100,0.3)",
                              background: "none",
                            }}
                            onClick={() => handleConfirmDeleteRequest(lead)}
                          >
                            {deleteConfirmId === lead.id
                              ? "Sure?"
                              : "Confirm Delete"}
                          </button>
                          <button
                            className={styles.btn}
                            style={{ padding: "5px 12px", fontSize: 9 }}
                            onClick={() => handleDismissDeleteRequest(lead)}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}

                      {/* Normal approve / reject for pending & flagged */}
                      {(lead.status === "pending" ||
                        lead.status === "flagged") && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            style={{
                              padding: "5px 12px",
                              fontSize: 9,
                              marginTop: 0,
                            }}
                            onClick={() => handleApproveLead(lead)}
                          >
                            Approve
                          </button>
                          <button
                            className={styles.btn}
                            style={{
                              padding: "5px 12px",
                              fontSize: 9,
                              color: "#e08080",
                              borderColor: "rgba(220,100,100,0.3)",
                            }}
                            onClick={() => handleRejectLead(lead)}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>

                    {/* ── Admin direct delete column ── */}
                    <td className={styles.td}>
                      {/* Don't show a separate delete button for leads already
                          in delete-requested state — handled via Actions above */}
                      {lead.status !== "delete" && (
                        <button
                          className={styles.editBtn}
                          style={{
                            color:
                              deleteConfirmId === lead.id
                                ? "#e05555"
                                : undefined,
                            borderColor:
                              deleteConfirmId === lead.id
                                ? "rgba(220,80,80,0.4)"
                                : undefined,
                            fontSize: 9,
                          }}
                          onClick={() => handleDeleteClick(lead)}
                          title="Delete lead"
                        >
                          {deleteConfirmId === lead.id ? "Sure?" : "🗑"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td
                      className={styles.td}
                      colSpan={10}
                      style={{ textAlign: "center", opacity: 0.5 }}
                    >
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={styles.rule} />
    </>
  );
}
